<script>
import i18n from "@/plugins/i18n";
import { mapActions } from 'vuex';

export default {
    data() {
        return {
            companies: [],
            companyWithUsers: null,
            auditedCompanyId: null,
        }
    },

    computed: {
        headers() {
            return [
                { key: 'company_name', text: i18n.t('auditor.companies.company_name'), flex: 1 },
                { key: 'company_email', text: i18n.t('auditor.companies.company_email'), flex: 1 },
                { key: 'company_phone', text: i18n.t('auditor.companies.company_phone'), flex: 1 },
                { key: 'users', text: i18n.t('auditor.companies.users'), flex: 1 },
                { key: 'select', text: i18n.t('auditor.companies.select'), flex: .5 },
            ];
        },

        authorityHeaders() {
            const lang = localStorage.getItem('lang') === 'tr' ? '_tr' : '';
            return [
                { key: 'firstname', text: i18n.t('auditor.companies.firstname'), flex: 1 },
                { key: 'surname', text: i18n.t('auditor.companies.surname'), flex: 1 },
                { key: 'email', text: i18n.t('auditor.companies.email'), flex: 1 },
                { key: 'role_name'.concat(lang), text: i18n.t('auditor.companies.role_name'), flex: 1 },
            ];
        }
    },

    mounted() {
        this._getAuditedCompanies();
    },

    methods: {
        ...mapActions('auditor', [
            'getAuditedCompanies',
        ]),

        _getAuditedCompanies() {
            this.getAuditedCompanies()
                .then(res => {
                    this.companies = res.data;
                    this.getAuditedCompany();
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        getAuditedCompany() {
            const id = Number(sessionStorage.getItem('audited_company_id'));
            this.setAuditedCompanyId(id);
        },

        // handlers //

        handleClickUsers(company) {
            this.setCompanyWithUsers(company.users);
            setTimeout(() => {
                this.$refs.authority.openPopup();
            }, 100);
        },

        handleSelectAuditedCompany(id) {
            if (!id) {
                console.error('Invalid parameter: ', id);
            }

            sessionStorage.setItem('audited_company_id', id);
            this.setAuditedCompanyId(id);
            this.$notify('info', i18n.t(('auditor.companies.change')), true);
        },

        // setters //

        setCompanyWithUsers(val) {
            this.companyWithUsers = val;
        },

        setAuditedCompanyId(id) {
            this.auditedCompanyId = id || null;
        },
    }
}
</script>

<template>
    <div class="audited-companies">
        <div class="audited-company-warning" v-if="!auditedCompanyId">
            {{ $t(('auditor.companies.none_selected')) }}
        </div>

        <div class="audited-companies-table">
            <div class="headers">
                <div class="header" v-for="header in headers" :key="header.key" 
                    :style="{flex: header.flex}"
                >
                    {{ header.text }}
                </div>
            </div>

            <div class="rows" v-for="company in companies" :key="company.audited_company_id">
                <div class="cell" v-for="header in headers" :key="header.key" :style="{flex: header.flex}">
                    <span v-if="header.key !== 'users' && header.key !== 'select' && header.key !== 'company_name'">
                        {{ company[header.key] }}
                    </span>
                    <span v-else-if="header.key === 'company_name'">
                        {{ company.company_name }} 
                        <span v-if="auditedCompanyId === company.audited_company_id" style="color: var(--sustainfinity-green);">
                            - {{ $t('auditor.companies.auditing') }}
                        </span>
                    </span>
                    <button v-else-if="header.key === 'users'" class="sustainability-users-button" @click="handleClickUsers(company)">
                        <i class="fa-solid fa-user-tie"></i>
                    </button>
                    <button v-else 
                        class="select-company-button" :class="{'selected': auditedCompanyId === company.audited_company_id}"
                        :disabled="auditedCompanyId === company.audited_company_id"
                        @click="handleSelectAuditedCompany(company.audited_company_id)"
                    >
                        {{ 
                            auditedCompanyId === company.audited_company_id ?
                            $t('auditor.companies._selected') : 
                            $t('auditor.companies._select')
                        }}
                    </button>
                </div>
            </div>
        </div>

        <sust-popup
            ref="authority"
            size="lg"
            @onHide="setCompanyWithUsers(null)"
            title=""
            :footer="false"
            v-if="companyWithUsers"
        >
            <div class="audited-companies-table" style="margin: 1em 0;">
                <div class="headers">
                    <div class="header" v-for="header in authorityHeaders" :key="header.key" 
                        :style="{flex: header.flex}"
                    >
                        {{ header.text }}
                    </div>
                </div>

                <div class="rows" v-for="(user, index) in companyWithUsers" :key="index">
                    <div class="cell" v-for="header in authorityHeaders" :key="header.key" :style="{flex: header.flex}">
                        {{ user[header.key] }}
                    </div>
                </div>
            </div>
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
    .audited-companies {
        width: 100%;
        padding: 1em;

        > .audited-company-warning {
            font-size: 1.2em;
            font-weight: bold;
            color: var(--invalid-input);
            padding: .25em 0 .5em 0;
        }
    }

    .audited-companies-table {
        border-radius: 1em;
        box-shadow: 0 .2em .5em .1em gray;
        border: 0;
        outline: thin solid var(--sustainfinity-navy);
        overflow: hidden;

        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        > .headers, .rows {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: flex-start;
        }

        > .headers {
            color: whitesmoke;
            background-color: var(--sustainfinity-navy);

            > .header {
                border-right: thin solid lightgray;

                &:last-of-type {
                    border: 0 !important;
                }
            }
        }

        > .rows {
            color: var(--sustainfinity-navy);
            background-color: whitesmoke;
        }
    }

    .header, .cell {
        padding: .5em;
        display: flex;
    }

    .sustainability-users-button {
        background-color: var(--sustainfinity-green);
        color: whitesmoke;
        border-radius: 100px;
        border: 0;
        outline: 0;
        padding: .5em 1.5em;
        margin: auto;
    }

    .select-company-button {
        background-color: var(--sustainfinity-faded-blue);
        color: var(--sustainfinity-blue);
        outline: thin solid var(--sustainfinity-green) !important;
        border-radius: .25em;
        border: 0;
        padding: .25em 1.5em;
        margin: auto;

        &:focus {
            outline: .15em solid var(--sustainfinity-green) !important;
        }

        &:active {
            outline: .15em solid var(--sustainfinity-green) !important;
            scale: .95;
        }
    }

    .selected {
        background-color: var(--sustainfinity-faded-green);
        color: var(--sustainfinity-faded-blue);
        outline: thin solid var(--sustainfinity-blue) !important;
        pointer-events: none !important;
    }


</style>